import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TimelineEventDto, TimelineEventVm } from '../events.models';
import { EventsService } from '../events.service';
import { DepartmentService } from 'src/app/services/department.service';
import { Department } from '../../../menu/menu.model';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { MainStore } from 'src/app/common/main.store';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { SeverityLevelPipe } from '../../../pipes/severity-level.pipe';
import { SearchInputComponent } from 'src/app/common/general/search-input/search-input.component';
import { CustomerService } from '../../customer/customer.service';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FeatureIconPipe } from '../pipes/feature-icon.pipe';
import { FeaturePopoverPipe } from '../pipes/feature-popover.pipe';
import { FeatureName } from 'src/app/common/feature-name';

@Component({
    selector: 'app-event-list',
    templateUrl: './event-list.component.html',
    styleUrl: './event-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DatePipe, SeverityLevelPipe, SearchInputComponent, NgbPopover, FeatureIconPipe, FeaturePopoverPipe]
})
export class EventListComponent implements OnInit, OnDestroy, AfterViewInit {
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  hasMore: boolean = true;
  searchText: string = '';
  events: TimelineEventVm[] = [];
  filterEvents: TimelineEventVm[] = [];
  private isDestroyed: boolean = false;

  private readonly pageSize: number = 20;
  private readonly days: number = 30;
  private currentDepartmentId: string | null = null;

  private lazyLoadObserver: IntersectionObserver | null = null;

  @ViewChild('eventTable') table: ElementRef | undefined;
  @ViewChild('scrollContainer') scrollContainer: ElementRef | undefined;
  @ViewChild('scrollSentinel') scrollSentinel: ElementRef | undefined;

  constructor(
    private eventService: EventsService,
    private commonService: CommonService,
    private departmentService: DepartmentService,
    private store: MainStore,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
  ) { }

  private toVm(item: TimelineEventDto): TimelineEventVm {
    const entityId = item.customDimensions?.entityId ?? '';
    return {
      ...item,
      customDimensions: {
        ...item.customDimensions,
        feature: item.customDimensions?.feature ?? '',
        user: item.customDimensions?.user ?? '',
        departmentGuid: item.customDimensions?.departmentGuid ?? '',
        logGroupGuid: item.customDimensions?.logGroupGuid ?? '',
        entityId,
      },
      itemId: item.itemId,
      children: [],
      showChildren: false,
      loadingChildren: false,
      userDepartment: '',
    };
  }

  async navigateToEntity(feature: string, entityId: string, domEvent?: Event): Promise<void> {
    domEvent?.stopPropagation();
    domEvent?.preventDefault();

    if (feature === FeatureName.Kunder) {
      await this.router.navigate(['/customer-dashboard', entityId]);
      return;
    } else if (feature === FeatureName.Skabeloner) {
      await this.router.navigate(['/template', entityId]);
      return;
    } else if (feature === FeatureName.Synkroniseringer) {
      await this.router.navigate(['/synchronization', entityId]);
      return;
    } else {
      console.warn('Navigation not supported for feature:', feature);
      return;
    }
  }

  search(): void {
    if (!this.searchText.trim()) {
      this.filterEvents = [...this.events];
      return;
    }
    this.filterEvents = this.events.filter(x =>
      x.message.toLowerCase().includes(this.searchText.toLowerCase())
      || (x.customDimensions.feature ?? '').toLowerCase().includes(this.searchText.toLowerCase()));
  }

  async ngOnInit(): Promise<void> {
    this.commonService.DepartmentChanged
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((a: Department, b: Department) => a?.id === b?.id)
      )
      .subscribe({
      next: async (department: Department) => {
        await this.changeDepartment(department.id);
      }
    });

    const selectedDepartment = this.departmentService.getDepartment();
    if (selectedDepartment != null) {
      await this.changeDepartment(selectedDepartment.id);
      this.expandEvent(this.route.snapshot.paramMap.get('eventid'));
    }


  }

  ngAfterViewInit(): void {
    this.setupLazyLoadObserver();
  }

  private setupLazyLoadObserver(): void {
    if (!this.scrollContainer?.nativeElement || !this.scrollSentinel?.nativeElement) {
      return;
    }

    this.lazyLoadObserver?.disconnect();

    this.lazyLoadObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }
        this.loadMore();
      },
      {
        root: this.scrollContainer.nativeElement,
        rootMargin: '150px',
        threshold: 0,
      }
    );

    this.lazyLoadObserver.observe(this.scrollSentinel.nativeElement);

    // If the initial page doesn't fill the viewport, the sentinel will already be visible
    // and some browsers won't trigger an intersection callback until layout/scroll changes.
    // Proactively fill the viewport so lazy-loading isn't dependent on screen size.
    setTimeout(() => {
      void this.fillViewportIfNeeded();
    }, 0);
  }

  async expandEvent(eventId: string | null): Promise<void> {
    if (!eventId) {
      return;
    }

    await this.ensureEventLoaded(eventId);

    // Wait for the view to render
    setTimeout(async () => {
      const event = this.events.find(e => e.itemId === eventId);
      if (!event) {
        return;
      }

      // Scroll to the event and set showchildren to true
      if (!this.table) {
        return;
      }
      const row = this.table.nativeElement.querySelector(`#event-${event.itemId}`);
      if (row) {
        await this.ToggleLogGroup(event);
        row.scrollIntoView({ behavior: 'instant', block: 'center' });
      }
    });
  }

  async changeDepartment(departmentId: string) : Promise<void> {
    // Avoid duplicate initial loads (DepartmentChanged often fires immediately).
    if (!departmentId) {
      return;
    }

    if (this.currentDepartmentId === departmentId) {
      if (this.isLoading || this.events.length > 0) {
        return;
      }
    }

    this.isLoading = true;
    this.currentDepartmentId = departmentId;
    this.hasMore = true;
    this.searchText = '';
    const dto = await this.eventService.getChangelogs(departmentId, this.days, undefined, this.pageSize);
    this.events = dto.map(x => this.toVm(x));
    this.filterEvents = [...this.events];
    this.hasMore = this.events.length === this.pageSize;
    this.isLoading = false;

    // Ensure we load enough to make the list scrollable on large screens.
    setTimeout(() => {
      void this.fillViewportIfNeeded();
    }, 0);
}

  async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore || !this.currentDepartmentId || this.isDestroyed) {
      return;
    }

    const lastEvent = this.events[this.events.length - 1];
    if (!lastEvent?.timestamp) {
      this.hasMore = false;
      return;
    }

    this.isLoadingMore = true;
    try {
      // Backend paging uses a strict "timestamp < beforeTimestamp" filter.
      // If multiple items share the same timestamp, paging *by timestamp alone* can otherwise
      // skip items at the boundary. We therefore set the cursor slightly *after* the last
      // loaded timestamp and de-duplicate client-side.
      const lastTimestamp = new Date(lastEvent.timestamp);
      const beforeTimeStamp = new Date(lastTimestamp.getTime() + 1);
      const nextPageDto = await this.eventService.getChangelogs(
        this.currentDepartmentId,
        this.days,
        beforeTimeStamp,
        this.pageSize,
      );

      const nextPage = nextPageDto.map(x => this.toVm(x));

      if (nextPage.length === 0) {
        this.hasMore = false;
        return;
      }

      const existingKeys = new Set(this.events.map(e => this.getEventDedupeKey(e)));
      const uniqueNextPage = nextPage.filter(e => {
        const key = this.getEventDedupeKey(e);
        if (existingKeys.has(key)) {
          return false;
        }
        existingKeys.add(key);
        return true;
      });

      this.events = [...this.events, ...uniqueNextPage];
      this.hasMore = nextPage.length === this.pageSize;
      this.search();
    } finally {
      this.isLoadingMore = false;

      // After appending, the container may still not be scrollable on large screens.
      // Keep filling until we can scroll or we reach the end.
      setTimeout(() => {
        void this.fillViewportIfNeeded();
      }, 0);
    }
  }

  onScroll(): void {
    if (!this.scrollContainer?.nativeElement) {
      return;
    }
    if (this.isLoading || this.isLoadingMore || !this.hasMore) {
      return;
    }

    const container = this.scrollContainer.nativeElement as HTMLElement;
    const thresholdPx = 150;
    const remaining = container.scrollHeight - (container.scrollTop + container.clientHeight);
    if (remaining <= thresholdPx) {
      void this.loadMore();
    }
  }

  private getEventDedupeKey(event: TimelineEventVm): string {
    const ts = String(event.timestamp);
    const feature = event.customDimensions?.feature ?? '';
    const message = event.message ?? '';
    return `${ts}|${feature}|${message}`;
  }

  private async fillViewportIfNeeded(): Promise<void> {
    if (!this.scrollContainer?.nativeElement) {
      return;
    }
    if (this.isLoading || this.isLoadingMore || !this.hasMore || this.isDestroyed) {
      return;
    }

    const container = this.scrollContainer.nativeElement as HTMLElement;
    const maxBatches = 50;
    let batches = 0;

    // If content height is less than (or very close to) the container height,
    // there is nothing to scroll yet, so keep loading additional pages.
    while (
      batches < maxBatches
      && this.hasMore
      && !this.isLoadingMore
      && !this.isDestroyed
      && container.scrollHeight <= container.clientHeight + 16
    ) {
      batches++;
      await this.loadMore();

      // Give the DOM a tick to render newly appended rows before re-checking.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
  }

  private async ensureEventLoaded(eventId: string): Promise<void> {
    // Safety cap to avoid accidental infinite paging loops.
    const maxPages = 25;
    let pagesLoaded = 0;

    while (pagesLoaded < maxPages && this.hasMore && !this.isDestroyed && !this.events.some(e => e.itemId === eventId)) {
      pagesLoaded++;
      await this.loadMore();
    }
  }

  async ToggleLogGroup(event: TimelineEventVm): Promise<void> {
    if ((event.childCount ?? 0) === 0) {
      return;
    }

    event.showChildren = !event.showChildren;
    event.loadingChildren = true;
    if (event.showChildren) {
      try {
        event.userDepartment = this.store.departmentList.get().find(x => x.id == (event.customDimensions.departmentGuid ?? ''))?.name ?? '';
        await this.FetchLogGroup(event);
      } catch (error) {
        console.error('Failed to fetch log group:', error);
      }
    }
    event.loadingChildren = false;
  }

  async FetchLogGroup(event: TimelineEventVm): Promise<void> {
    if (event.customDimensions.logGroupGuid == null || event.customDimensions.logGroupGuid == '' || event.customDimensions.logGroupGuid == this.commonService.guidEmpty()) return;
    if (event.children.length > 0) return;
    event.children = await this.eventService.getLogGroupEvents(event.customDimensions.logGroupGuid, 30);
  }

  destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.lazyLoadObserver?.disconnect();
    this.lazyLoadObserver = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

}
